import type { JSX } from 'react';
import { useStore } from '@/state/store';
import { Panel, Row } from '@/components/ui/Panel';
import { ASSIGNMENT_LABEL, CREASE_ASSIGNMENTS, type CreaseAssignment } from '@/origami/types';
import { resolveTargetAngle } from '@/origami/angles';
import { MAX_FOLD_ANGLE, toDegrees, toRadians } from '@/constants/numeric';
import { dist2 } from '@/geometry/vec2';

/**
 * Per-crease control. The target fold angle is editable, which turns the app
 * into a mechanism explorer: lock a crease, drive another, and watch what the
 * rest of the linkage is forced to do.
 */
export function CreaseInspector(): JSX.Element | null {
  const edge = useStore((s) => s.inspectedEdge);
  const pattern = useStore((s) => s.pattern);
  const frame = useStore((s) => s.frame);
  const sim = useStore((s) => s.sim);
  const analysis = useStore((s) => s.analysis);
  const setEdgeTarget = useStore((s) => s.setEdgeTarget);
  const toggleEdgeLock = useStore((s) => s.toggleEdgeLock);
  const setSelection = useStore((s) => s.setSelection);
  const assignSelection = useStore((s) => s.assignSelection);
  const inspectVertex = useStore((s) => s.inspectVertex);

  if (edge === null || edge >= pattern.edgesVertices.length) return null;

  const assignment = pattern.edgesAssignment[edge];
  const [a, b] = pattern.edgesVertices[edge];
  const length = dist2(pattern.verticesCoords[a], pattern.verticesCoords[b]);
  const override = sim.edgeTargetOverride[edge];
  const target =
    override !== undefined ? override : resolveTargetAngle(assignment, pattern.edgesFoldAngle[edge]);
  const current = frame?.edgeFoldAngles[edge];
  const locked = sim.lockedEdges.includes(edge);
  const faces = analysis.topology.edgeFaces[edge];
  const p1 = pattern.verticesCoords[a];
  const p2 = pattern.verticesCoords[b];
  const heading = toDegrees(Math.atan2(p2.y - p1.y, p2.x - p1.x));

  return (
    <Panel title={`Crease ${edge}`}>
      <Row label="Type" value={ASSIGNMENT_LABEL[assignment]} />
      <Row label="Length" value={length.toFixed(6)} />
      <Row label="Heading" value={`${heading.toFixed(3)}°`} />
      <Row
        label="Endpoints"
        value={
          <>
            <button className="btn tiny" onClick={() => inspectVertex(a)}>
              v{a}
            </button>{' '}
            <button className="btn tiny" onClick={() => inspectVertex(b)}>
              v{b}
            </button>
          </>
        }
      />
      <Row
        label="Faces"
        value={`${faces[0] < 0 ? '—' : faces[0]} / ${faces[1] < 0 ? '—' : faces[1]}`}
        title="The two faces this crease hinges. A dash means there is no paper on that side."
      />
      <Row
        label="Current angle"
        value={
          current !== undefined && Number.isFinite(current)
            ? `${toDegrees(current).toFixed(2)}°`
            : 'not hinged'
        }
        tone={current !== undefined && Number.isFinite(current) ? undefined : 'dim'}
      />
      <Row label="Target at 100%" value={`${toDegrees(target).toFixed(2)}°`} />

      <div style={{ marginTop: 6 }}>
        <input
          type="range"
          min={-180}
          max={180}
          step={0.5}
          value={toDegrees(target)}
          disabled={assignment === 'B' || assignment === 'C'}
          onChange={(e) => setEdgeTarget(edge, toRadians(Number(e.target.value)))}
        />
        <div className="row">
          <span className="k faint">−180°</span>
          <span className="v faint">+180°</span>
        </div>
      </div>

      <div className="btn-row">
        <button className="btn tiny" onClick={() => setEdgeTarget(edge, null)}>
          Reset target
        </button>
        <button
          className="btn tiny"
          aria-pressed={locked}
          onClick={() => toggleEdgeLock(edge)}
          title="Hold this crease at its current angle while the rest of the pattern moves."
        >
          {locked ? 'Unlock' : 'Lock'}
        </button>
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <span className="k">Assignment</span>
      </div>
      <div className="btn-row">
        {CREASE_ASSIGNMENTS.filter((x) => x !== 'J').map((x) => (
          <button
            key={x}
            className="btn tiny"
            aria-pressed={x === assignment}
            style={x === assignment ? { borderColor: 'var(--accent)' } : undefined}
            onClick={() => {
              setSelection([{ kind: 'edge', index: edge }]);
              assignSelection(x as CreaseAssignment);
            }}
          >
            {x}
          </button>
        ))}
      </div>
      <p className="note">
        Target angles are clamped to ±{toDegrees(MAX_FOLD_ANGLE).toFixed(1)}°. A dihedral angle of
        exactly 180° is the branch point of the atan2 measurement, so the solver stops just short of
        it; see docs/MATHEMATICS.md.
      </p>
    </Panel>
  );
}
